/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import store from 'js/lib/coursera.store';
import waitForGraphql from 'js/lib/waitForGraphQL';

import type { StandaloneCheckboxProps, Theme } from '@coursera/cds-core';
import { Link, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import type {
  IdVerificationsQuery as IdVerificationsQueryType,
  IdVerificationsQuery_IdVerificationsV1Resource_me_elements as UserVerificationData,
} from 'bundles/item/components/__generated__/IdVerificationsQuery';
import { org_coursera_userverification_idverification_IdVerificationApprovalSource as IdVerificationApprovalSource } from 'bundles/item/components/__generated__/globalTypes';
import { getUserFullName } from 'bundles/verification/data/VerificationUtils';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  honorCodeAgreement: (theme: Theme) =>
    css({
      paddingBottom: 'var(--cds-spacing-200)',
    }),
  agreementContainer: (theme: Theme) =>
    css({
      marginTop: 'var(--cds-spacing-100)',
    }),
  checkbox: css`
    .cds-checkboxAndRadio-label {
      margin-bottom: 0 !important;
    }
  `,
  label: (theme: Theme) =>
    css({
      margin: `calc(var(--cds-spacing-100) * -1) 0 0 0`,
      // this is needed because labels only allow phrasing content as children (ie inline)
      display: 'inline-block',
      ':hover span': {
        color: `var(--cds-color-blue-800) !important;`,
      },
    }),
  legalName: (theme: Theme) =>
    css({
      minHeight: 80,
      // gap between checkbox/label + width of checkbox
      marginLeft: 'calc(var(--cds-spacing-150) + 24px)',
    }),
  nameInput: (theme: Theme) =>
    css({
      paddingLeft: 'var(--cds-spacing-200)',
      minHeight: 40,
      width: 300,
      marginBottom: 'var(--cds-spacing-150)',
      '::placeholder': {
        color: 'var(--cds-color-grey-500)',
        opacity: 1,
      },
    }),
  asterisk: css`
    color: var(--cds-color-red-700);
  `,
};

type PropsFromGraphql = {
  userVerificationInformation: Partial<UserVerificationData> | null | undefined;
};

type PropsFromCaller = {
  onLoaded?: () => void;
  onAgreementComplete?: (name: string) => void;
  onAgreementIncomplete?: () => void;
  isShownInsideModal?: boolean;
  showNextGenAssignmentsStyling?: boolean;
} & Pick<StandaloneCheckboxProps, 'validationStatus'>;

type PropsToComponent = PropsFromGraphql & PropsFromCaller;

type State = {
  agreementChecked: boolean;
  fullName: string;
  userVerified: boolean;
};

class HonorCodeAgreement extends React.Component<PropsToComponent, State> {
  declare nameInput: HTMLElement | null | undefined;

  constructor(props: PropsToComponent, context: $TSFixMe) {
    super(props, context);
    const { userVerificationInformation } = props;
    const userVerified = userVerificationInformation?.verifiedBy !== IdVerificationApprovalSource.NONE;
    const fullName = userVerified
      ? getUserFullName(userVerificationInformation)
      : store.get('temporaryVerifiedProfileFullName');
    this.state = {
      agreementChecked: false,
      fullName,
      userVerified,
    };
  }

  componentDidMount() {
    const { onLoaded } = this.props;
    // Let callers know we've loaded, in case they want to show a loading screen to prevent a
    // situation where everything but the honor code agreement is visible.
    // Since the graphql call is a "wait for", we've "loaded" as soon as this component is mounted.
    onLoaded?.();
  }

  handleCheckboxChange = () => {
    this.setState(
      (prevState) => ({
        agreementChecked: !prevState.agreementChecked,
      }),
      () => {
        if (!this.state.userVerified && this.state.agreementChecked && this.nameInput) {
          this.nameInput.focus();
        }

        this.checkForAgreementCompletion(this.state.agreementChecked, this.state.fullName);
      }
    );
  };

  handleNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const fullName = event.target.value;

    this.setState({
      fullName,
    });

    store.set('temporaryVerifiedProfileFullName', fullName);

    this.checkForAgreementCompletion(this.state.agreementChecked, fullName);
  };

  checkForAgreementCompletion(checkedState: boolean, fullName: string) {
    if (checkedState && fullName) {
      this.props.onAgreementComplete?.(fullName);
    } else {
      this.props.onAgreementIncomplete?.();
    }
  }

  render() {
    const { fullName, userVerified } = this.state;
    const { validationStatus, showNextGenAssignmentsStyling = false } = this.props;
    const honorCodeLink = 'https://learner.coursera.help/hc/articles/209818863';

    const agreementStringWithName = (
      <FormattedHTMLMessage
        message={_t(
          `I, <b class="honor-code-agreement full-name pii-hide">{fullName}</b>, understand that submitting work that isn’t my own may result in permanent failure of this course or deactivation of my Coursera account.`
        )}
        fullName={fullName}
      />
    );
    const agreementStringNoName = _t(
      `I understand that submitting work that isn’t my own may result in permanent failure of this course or deactivation of my Coursera account.`
    );

    const agreementString = !userVerified ? agreementStringNoName : agreementStringWithName;

    let validationLabel;
    if (validationStatus === 'error') {
      validationLabel = fullName
        ? _t('Please check the box to continue')
        : _t('Please fill in your legal name and check the box to continue');
    }

    return (
      <div data-testid="HonorCodeAgreement" css={styles.honorCodeAgreement}>
        {!showNextGenAssignmentsStyling && (
          <div>
            <Typography2 variant="subtitleMedium" component="span">
              {_t('Coursera Honor Code')}
            </Typography2>
            &nbsp;&nbsp;
            <Link
              href={honorCodeLink}
              target="_blank"
              rel="noopener noreferrer"
              typographyVariant="bodyPrimary"
              aria-label={_t('Learn more about Coursera’s Honor Code (opens in a new tab)')}
              icon={<ExternalLinkIcon size="medium" />}
              iconPosition="after"
            >
              {_t('Learn more')}
            </Link>
          </div>
        )}
        <div css={styles.agreementContainer}>
          {showNextGenAssignmentsStyling ? (
            <div>
              <div css={{ paddingBottom: 'var(--cds-spacing-100)' }}>{agreementString}</div>
              <Link
                href={honorCodeLink}
                target="_blank"
                rel="noopener noreferrer"
                typographyVariant="bodyPrimary"
                aria-label={_t('Learn more about Coursera’s Honor Code (opens in a new tab)')}
                icon={<ExternalLinkIcon size="medium" />}
                iconPosition="after"
              >
                {_t('Learn more')}
              </Link>
            </div>
          ) : (
            <div data-testid="agreement-checkbox">
              <StandaloneCheckbox
                // @ts-expect-error
                label={
                  <span css={styles.label}>
                    {agreementString}
                    <span css={styles.asterisk}>*</span>
                  </span>
                }
                css={styles.checkbox}
                data-testid="agreement-standalone-checkbox"
                inputProps={{ 'aria-labelledby': 'check-agree' }}
                id="agreement-checkbox"
                onChange={this.handleCheckboxChange}
                // @ts-expect-error the label type is string, but we need a component for a11y
                validationLabel={
                  validationLabel ? (
                    <span
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabIndex={-1}
                      ref={(el) => {
                        if (el) {
                          el.focus();
                        }
                      }}
                    >
                      {validationLabel}
                    </span>
                  ) : null
                }
                validationStatus={validationStatus}
                aria-required="true"
                supportText={_t('You must select the checkbox in order to submit the assignment')}
              />
            </div>
          )}
          <div data-testid="agreement-text">
            {!userVerified && (
              <div data-testid="legal-name" css={styles.legalName}>
                <div data-testid="input-area">
                  <input
                    placeholder={_t('Enter your legal name')}
                    aria-label={_t('Enter your legal name')}
                    aria-describedby="legal-name-instruction"
                    defaultValue={fullName}
                    css={styles.nameInput}
                    ref={(nameInput) => {
                      this.nameInput = nameInput;
                    }}
                    onChange={this.handleNameInputChange}
                    aria-required="true"
                  />

                  <div id="legal-name-instruction" className="input-caption caption-text color-secondary-text">
                    {_t('Use the name on your government issued ID')} <span css={styles.asterisk}>*</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

type IdVerificationsQueryVariables = {};

export const IdVerificationsQuery = gql`
  query IdVerificationsQuery {
    IdVerificationsV1Resource {
      me {
        elements {
          id
          firstName
          middleName
          lastName
          verifiedBy
        }
      }
    }
  }
`;

export default waitForGraphql<
  PropsFromCaller,
  IdVerificationsQueryType,
  IdVerificationsQueryVariables,
  PropsToComponent
>(IdVerificationsQuery, {
  props: ({ data, ownProps }) => {
    return {
      ...ownProps,
      userVerificationInformation: data?.IdVerificationsV1Resource?.me?.elements?.[0] || {
        verifiedBy: IdVerificationApprovalSource.NONE,
      },
    };
  },
})(HonorCodeAgreement);
